import { useEffect, useState } from "react";
import { BellRing, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PANEL, inputCls } from "./shared";
import { AccountSecurityPanel } from "./AccountSecurityPanel";

const BOOKING_FIELDS = [
  ["booking_url", "Main booking link (default for all pathways)", "booking-url-input"],
  ["booking_url_legacy", "Legacy / Estate Planning Review", "booking-url-legacy-input"],
  ["booking_url_family", "Family Protection Review", "booking-url-family-input"],
  ["booking_url_medicare", "Medicare Coverage Review", "booking-url-medicare-input"],
];

function fromSettings(s) {
  return {
    booking_url: s?.booking_url || "",
    booking_url_legacy: s?.booking_url_legacy || "",
    booking_url_family: s?.booking_url_family || "",
    booking_url_medicare: s?.booking_url_medicare || "",
    digest_email: s?.digest_email || "",
    alert_email: s?.alert_email || "",
    ghl_webhook_url: s?.ghl_webhook_url || "",
    licensed_states: (s?.licensed_states || []).join(", "),
  };
}

function Section({ title, children, help }) {
  return (
    <>
      <p className="mt-5 text-sm font-bold text-navy mb-1.5">{title}</p>
      {help && <p className="text-xs text-slate-500 mb-3">{help}</p>}
      {children}
    </>
  );
}

export function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState(() => fromSettings(settings));
  useEffect(() => setForm(fromSettings(settings)), [settings]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const run = async (action, successMsg, fallbackMsg) => {
    try {
      const msg = await action();
      toast.success(typeof msg === "string" && msg ? msg : successMsg);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err) || fallbackMsg);
    }
  };

  const save = () => run(() => api.put("/admin/settings", form), "Settings saved.", "Could not save settings.");
  const testGhl = () => run(async () => {
    await api.put("/admin/settings", form);
    const r = await api.post("/admin/ghl/test");
    return `Test lead sent — GoHighLevel responded ${r.data.status_code}.`;
  }, "", "GHL test failed.");
  const sendDigest = () => run(async () => {
    await api.put("/admin/settings", { digest_email: form.digest_email });
    await api.post("/admin/digest/send");
  }, "Digest sent — check your inbox.", "Could not send digest.");

  return (
    <div className={PANEL} data-testid="admin-settings">
      <h2 className="font-serif text-2xl font-semibold text-navy mb-1.5">Settings</h2>
      <p className="text-sm text-slate-500 mb-4">Booking links by pathway. Each results page opens its own review calendar; any pathway left blank falls back to the main link.</p>
      <div className="grid gap-3">
        {BOOKING_FIELDS.map(([key, label, tid]) => (
          <label key={key} className="block">
            <span className="block text-xs font-bold text-navy mb-1">{label}</span>
            <input placeholder={key === "booking_url" ? "https://calendly.com/your-link" : "Optional — uses the main link until you add one"} className={inputCls} value={form[key]} onChange={set(key)} data-testid={tid} />
          </label>
        ))}
        <button onClick={save} data-testid="save-settings-button" className="min-h-[46px] rounded-xl bg-navy text-cream font-bold text-sm px-6 hover:bg-navy-light transition-colors inline-flex items-center justify-center gap-2 sm:w-fit">
          <Save className="w-4 h-4" /> Save Settings
        </button>
      </div>

      <Section
        title="Licensed States (Insurance Services)"
        help="Two-letter state codes where Legacy Wave Consulting is currently authorized to offer insurance services (e.g. TX, GA, FL). Visitors outside these states still receive their educational report, with a clear note that insurance availability varies by state. Leave blank while confirming licensing."
      >
        <input placeholder="e.g. TX, GA, FL" className={`${inputCls} mb-4`} value={form.licensed_states} onChange={set("licensed_states")} data-testid="licensed-states-input" />
      </Section>

      <Section title="Instant Lead Alerts" help="Get an email the moment a new lead completes their Legacy Checkup — so you can follow up while it's warm. Leave blank to use your digest email (or admin email).">
        <input type="email" placeholder="Alert recipient email (optional)" className={`${inputCls} mb-4`} value={form.alert_email} onChange={set("alert_email")} data-testid="alert-email-input" />
      </Section>

      <Section title="Monthly Partner Performance Digest" help="On the 1st of each month, we'll email a summary of which partners and campaigns generated the most leads (sent to your admin email if left blank).">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input type="email" placeholder="Digest recipient email (optional)" className={`${inputCls} flex-1`} value={form.digest_email} onChange={set("digest_email")} data-testid="digest-email-input" />
          <button onClick={sendDigest} data-testid="send-digest-button" className="min-h-[46px] rounded-xl bg-gold text-navy font-bold text-sm px-6 hover:bg-gold-hover transition-colors inline-flex items-center justify-center gap-2">
            <BellRing className="w-4 h-4" /> Send Digest Now
          </button>
        </div>
        {settings?.last_digest_sent_at && (
          <p className="mb-4 text-xs text-slate-500">Last digest sent {new Date(settings.last_digest_sent_at).toLocaleString()}</p>
        )}
      </Section>

      <Section title="GoHighLevel CRM Sync" help="Paste your GoHighLevel inbound webhook URL (GHL → Workflows → Inbound Webhook Trigger). Every new lead — with score, tags, referral source, and campaign — is sent here automatically. Stored server-side only; never exposed to visitors.">
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <input type="url" placeholder="https://services.leadconnectorhq.com/hooks/..." className={`${inputCls} flex-1`} value={form.ghl_webhook_url} onChange={set("ghl_webhook_url")} data-testid="ghl-webhook-input" />
          <button onClick={testGhl} data-testid="ghl-test-button" className="min-h-[46px] rounded-xl bg-navy text-cream font-bold text-sm px-6 hover:bg-navy-light transition-colors">
            Send Test Lead
          </button>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Status:{" "}
          <span className={`font-bold ${settings?.ghl_webhook_configured ? "text-[#15803D]" : "text-slate-500"}`} data-testid="ghl-status">
            {settings?.ghl_webhook_configured ? `Connected${settings?.ghl_last_test_status ? ` — last test ${settings.ghl_last_test_status}` : ""}` : "Not configured — leads are stored securely here until you connect GoHighLevel."}
          </span>
        </p>
      </Section>

      <Section title="Account & Security" help="Manage the login email and password for the Legacy Wave administrator account. Changing either never creates a new account or disconnects your data.">
        <AccountSecurityPanel />
      </Section>
    </div>
  );
}
